import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useBrainStore } from '@/state/brainStore';
import { Search, ArrowRight, Users } from 'lucide-react';

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function CommandKSearch({ open, onClose }: Props) {
  const navigate = useNavigate();
  const customers = useBrainStore((s) => s.customers);
  const tail = useBrainStore((s) => s.tailRecords);
  const [q, setQ] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setQ('');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const heroList = useMemo(() => Object.values(customers), [customers]);

  const results = useMemo(() => {
    if (!q.trim()) {
      return heroList.slice(0, 8).map((c) => ({
        id: c.id,
        title: `${c.firstName} ${c.lastName}`,
        subtitle: `${c.phone} · ${c.suburb}`,
        score: c.leadScore,
        hero: true,
      }));
    }
    const needle = q.trim().toLowerCase();
    const heroMatches = heroList
      .filter((c) => {
        const hay = `${c.firstName} ${c.lastName} ${c.phone} ${c.suburb}`.toLowerCase();
        return hay.includes(needle);
      })
      .slice(0, 6)
      .map((c) => ({
        id: c.id,
        title: `${c.firstName} ${c.lastName}`,
        subtitle: `${c.phone} · ${c.suburb}`,
        score: c.leadScore,
        hero: true,
      }));
    const tailMatches = tail
      .filter((t) => {
        const hay = `${t.firstName} ${t.lastName} ${t.phone} ${t.suburb}`.toLowerCase();
        return hay.includes(needle);
      })
      .slice(0, 6 - heroMatches.length)
      .map((t) => ({
        id: t.id,
        title: `${t.firstName} ${t.lastName}`,
        subtitle: `${t.phone} · ${t.suburb}`,
        score: 40,
        hero: false,
      }));
    return [...heroMatches, ...tailMatches];
  }, [q, heroList, tail]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Enter') {
        const first = results[0];
        if (first) {
          navigate(`/customer/${first.id}`);
          onClose();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, results, navigate, onClose]);

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-overlay/80 backdrop-blur-sm flex items-start justify-center pt-32 px-6"
          onClick={onClose}
          data-testid="cmdk-search"
        >
          <motion.div
            initial={{ y: -8, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -8, opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="bg-ink-deep border border-gold-dim/40 rounded-xl w-full max-w-xl shadow-ink-soft"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 px-4 py-3 border-b border-gold-dim/20">
              <Search size={14} className="text-gold-bright" />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search customers by name, phone, or suburb"
                className="flex-1 bg-transparent border-none outline-none font-serif text-cream text-sm placeholder:text-cream-soft/60"
                data-testid="cmdk-input"
              />
              <span className="font-inter text-[10px] text-cream-soft uppercase tracking-widest">
                Esc
              </span>
            </div>
            <ul className="py-1 max-h-80 overflow-y-auto">
              {results.length === 0 ? (
                <li className="px-4 py-6 text-center font-serif text-cream-soft text-sm">
                  No customers match. Try a different name or phone.
                </li>
              ) : null}
              {results.map((r, idx) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => {
                      navigate(`/customer/${r.id}`);
                      onClose();
                    }}
                    className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-ink-soft/50 transition"
                    data-testid={`cmdk-result-${r.id}`}
                  >
                    <div className="flex items-center gap-3">
                      {r.hero ? (
                        <span className="h-2 w-2 rounded-full bg-gold-bright" />
                      ) : (
                        <span className="h-2 w-2 rounded-full bg-cream-soft/30" />
                      )}
                      <div className="text-left">
                        <p className="font-inter text-sm text-cream">{r.title}</p>
                        <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-0.5">
                          {r.subtitle}
                        </p>
                      </div>
                    </div>
                    <span className="flex items-center gap-2 text-cream-soft text-[10px] font-inter uppercase tracking-widest">
                      score {r.score}
                      {idx === 0 ? <ArrowRight size={12} className="text-gold-bright" /> : null}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            <div className="px-4 py-2 border-t border-gold-dim/20 flex items-center justify-between text-[10px] text-cream-soft font-inter uppercase tracking-widest">
              <span className="flex items-center gap-1">
                <Users size={10} /> Customers
              </span>
              <span>Enter to open top result</span>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
